import { TEST_IDS } from '@argus-labs/auth-shared/test-ids'
import { useSuspenseQuery } from '@tanstack/react-query'
import { createFileRoute, Link, type LinkProps, useNavigate } from '@tanstack/react-router'
import { EditIcon } from '@/components/decorative/edit-icon'
import { Button } from '@/components/ui/button'
import { PageCard } from '@/components/ui/page-card'
import { Text, text } from '@/components/ui/text'
import { authClient } from '@/lib/auth-client'
import { getSessionQueryOptions, invalidateSessionQuery } from '@/lib/session'
import { generateTitle } from '@/lib/utils'
import { resetPosthog } from '@/telemetry/posthog'

export const Route = createFileRoute('/_auth/account')({
  component: Account,
  head: () => ({ meta: [{ title: generateTitle('My Account') }] }),
})

function Account() {
  const {
    data: { data },
  } = useSuspenseQuery(getSessionQueryOptions())
  const navigate = useNavigate()

  if (!data) return null // should never happen because of beforeLoad in _auth.tsx

  const handleLogout = async () => {
    const { data, error } = await authClient.signOut()
    if (error || !data.success) return

    invalidateSessionQuery()
    resetPosthog()
    navigate({ to: '/login', replace: true })
  }

  return (
    <PageCard title="Account Information" description="Here’s your detailed account information.">
      <dl className="flex flex-col gap-4">
        <div>
          <Key>Full Name</Key>
          <Value linkTo="/edit/name" testId={TEST_IDS.accountName} linkAriaLabel="Edit Name">
            {data.user.name}
          </Value>
        </div>

        <div>
          <Key>Email address</Key>
          <Value linkTo="/edit/email" testId={TEST_IDS.accountEmail} linkAriaLabel="Edit Email">
            <div className="flex items-center gap-2">{data.user.email}</div>
          </Value>
        </div>

        <div className="py-8">
          <Button type="button" flex className="w-full" variant="link" onClick={handleLogout}>
            Log Out
          </Button>
        </div>
      </dl>
    </PageCard>
  )
}

const Key = ({ children }: { children: React.ReactNode }) => (
  <dt
    className={text({
      font: 'nbArchitekt',
      color: 'white',
      className: 'text-[0.625rem] md:text-xs leading-[1.2] tracking-[0.06em]',
    })}
  >
    {children}
  </dt>
)

const Value = ({
  children,
  testId,
  linkTo,
  linkAriaLabel,
}: {
  children: React.ReactNode
  testId?: string
  linkTo?: LinkProps['to']
  linkAriaLabel?: string
}) => (
  <dd className="mt-2 flex items-center justify-between gap-4">
    <Text
      font="beausite"
      color="white"
      block
      className="text-sm md:text-xl leading-[1.4] tracking-[0.01em]"
      data-testid={testId}
    >
      {children}
    </Text>

    {/* Add padding + negative margin for larger tap target while maintaining the same general layout */}
    <Link to={linkTo} className="p-2 -m-2" aria-label={linkAriaLabel}>
      <EditIcon className="size-6" />
    </Link>
  </dd>
)
